/**
 * Messagerie formateur ↔ étudiants.
 *
 * - Liste des threads (par étudiant ou par classe broadcast)
 * - Composer : individuel (matricule) OU broadcast classe
 * - Détail thread : affiche le fil d'une conversation
 */
import { useEffect, useMemo, useState } from 'react';
import {
  MessageSquare, User, Users as UsersIcon, Send, Plus, X,
  Loader2, ArrowLeft, Search, GraduationCap,
} from 'lucide-react';
import { FormateurLayout } from '../FormateurLayout';
import {
  useFormateurMeQuery,
  useListerMessagesFormateurQuery,
  useEnvoyerMessageFormateurMutation,
  useMessageThreadFormateurQuery,
  useMesEtudiantsQuery,
} from '../../services/formateurApi';

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function Messages() {
  const [composing, setComposing] = useState(false);
  const [activeThread, setActiveThread] = useState<{ kind: 'etudiant'; matricule: string; libelle: string }
                                                  | { kind: 'classe'; classe: string }
                                                  | null>(null);

  const { data: meData } = useFormateurMeQuery();
  const { data, isLoading, refetch } = useListerMessagesFormateurQuery();

  if (activeThread) {
    return (
      <ThreadView selection={activeThread}
        onBack={() => { setActiveThread(null); refetch(); }} />
    );
  }

  if (composing) {
    return (
      <ComposerView
        classes={meData?.classes || []}
        onClose={() => setComposing(false)}
        onSent={() => { setComposing(false); refetch(); }}
      />
    );
  }

  const threads = data?.results || [];

  return (
    <FormateurLayout title="Messagerie">
      <button onClick={() => setComposing(true)}
        className="w-full px-4 py-3 mb-3 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg inline-flex items-center justify-center gap-2">
        <Plus size={16} /> Nouveau message
      </button>

      {isLoading && (
        <div className="flex items-center justify-center gap-2 py-10 text-gray-400 text-sm">
          <Loader2 size={16} className="animate-spin" /> Chargement…
        </div>
      )}

      {!isLoading && threads.length === 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
          <MessageSquare size={36} strokeWidth={1.3} className="mx-auto mb-3 text-gray-500" />
          <div className="text-sm font-medium text-gray-300">Aucun message envoyé</div>
          <p className="text-xs text-gray-500 mt-1">
            Cliquez sur « Nouveau message » pour écrire à un étudiant ou à une classe entière.
          </p>
        </div>
      )}

      {threads.length > 0 && (
        <div className="space-y-2">
          {threads.map(t => (
            <button key={t.cle}
              onClick={() => {
                if (t.type === 'classe') {
                  setActiveThread({ kind: 'classe', classe: t.classe });
                } else {
                  setActiveThread({ kind: 'etudiant', matricule: t.matricule, libelle: t.matricule });
                }
              }}
              className="w-full text-left bg-gray-900 border border-gray-800 hover:border-gray-700 rounded-xl p-3 transition">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  t.type === 'classe'
                    ? 'bg-purple-900/40 text-purple-300'
                    : 'bg-blue-900/40 text-blue-300'
                }`}>
                  {t.type === 'classe' ? <UsersIcon size={18} /> : <User size={18} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-white truncate">
                      {t.type === 'classe' ? `Classe ${t.classe}` : t.matricule}
                    </div>
                    <span className="text-[10px] text-gray-500 flex-shrink-0">{fmtDate(t.dernier_date)}</span>
                  </div>
                  <div className="text-[11px] text-gray-400 mt-0.5 truncate">{t.dernier_message}</div>
                  <div className="text-[10px] text-gray-500 mt-1">
                    {t.nb_messages} message{t.nb_messages > 1 ? 's' : ''}
                  </div>
                </div>
                {t.nb_non_lus_par_formateur > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold bg-blue-500 text-white rounded-full px-1.5 flex-shrink-0">
                    {t.nb_non_lus_par_formateur}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </FormateurLayout>
  );
}

// ──────────────────────────────────────────────────────────────────
// Composer
// ──────────────────────────────────────────────────────────────────

function ComposerView({ classes, onClose, onSent }: {
  classes: Array<{ code: string; option: string }>;
  onClose: () => void;
  onSent: () => void;
}) {
  const [mode, setMode] = useState<'individuel' | 'classe'>('individuel');
  const [classeSel, setClasseSel] = useState('');
  const [matricule, setMatricule] = useState('');
  const [searchEtu, setSearchEtu] = useState('');
  const [sujet, setSujet] = useState('');
  const [contenu, setContenu] = useState('');
  const [matiereLibelle, setMatiereLibelle] = useState('');
  const [error, setError] = useState('');
  const [envoyer, { isLoading }] = useEnvoyerMessageFormateurMutation();

  const { data: etuData } = useMesEtudiantsQuery(
    classeSel ? { classe: classeSel } : undefined,
    { skip: mode !== 'individuel' || !classeSel },
  );

  const etudiantsFiltres = useMemo(() => {
    const arr = etuData?.results || [];
    const q = searchEtu.toLowerCase().trim();
    if (!q) return arr;
    return arr.filter(e =>
      e.matricule.toLowerCase().includes(q)
      || e.nom.toLowerCase().includes(q)
      || e.prenoms.toLowerCase().includes(q),
    );
  }, [etuData, searchEtu]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (contenu.trim().length < 2) { setError('Le message est vide.'); return; }
    if (mode === 'individuel' && !matricule) { setError('Sélectionnez un étudiant.'); return; }
    if (mode === 'classe' && !classeSel) { setError('Sélectionnez une classe.'); return; }

    try {
      await envoyer({
        ...(mode === 'individuel' ? { matricule_etudiant: matricule } : { classe: classeSel }),
        sujet: sujet.trim() || undefined,
        contenu: contenu.trim(),
        matiere_libelle: matiereLibelle.trim() || undefined,
      }).unwrap();
      onSent();
    } catch (err: any) {
      setError(err?.data?.error || err?.message || 'Erreur d\'envoi.');
    }
  };

  return (
    <FormateurLayout title="Nouveau message">
      <button onClick={onClose}
        className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1 mb-3">
        <ArrowLeft size={13} /> Annuler
      </button>

      <form onSubmit={handleSend} className="space-y-4">
        {/* Destinataire : individuel ou classe */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">
            Destinataire
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <button type="button" onClick={() => { setMode('individuel'); setMatricule(''); }}
              className={`px-2.5 py-2 text-xs font-semibold rounded-lg border ${
                mode === 'individuel'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-gray-900 text-gray-300 border-gray-800 hover:border-gray-600'
              }`}>
              <User size={13} className="inline mr-1" /> Un étudiant
            </button>
            <button type="button" onClick={() => { setMode('classe'); setMatricule(''); }}
              className={`px-2.5 py-2 text-xs font-semibold rounded-lg border ${
                mode === 'classe'
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-gray-900 text-gray-300 border-gray-800 hover:border-gray-600'
              }`}>
              <UsersIcon size={13} className="inline mr-1" /> Toute une classe
            </button>
          </div>
        </div>

        {/* Sélecteur classe */}
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
            Classe <span className="text-red-400">*</span>
          </label>
          <select value={classeSel} onChange={(e) => setClasseSel(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-gray-900 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500"
            required>
            <option value="">— Choisir une classe —</option>
            {classes.map(c => (
              <option key={c.code} value={c.code}>{c.code} — {c.option}</option>
            ))}
          </select>
          {classes.length === 0 && (
            <div className="text-[11px] text-amber-400 mt-1">
              Aucune classe trouvée pour vous. Vérifiez vos attributions matière-formateur côté scolarité.
            </div>
          )}
        </div>

        {/* Sélecteur étudiant (si individuel) */}
        {mode === 'individuel' && classeSel && (
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
              Étudiant <span className="text-red-400">*</span>
            </label>
            <div className="relative mb-1.5">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input type="text" value={searchEtu} onChange={(e) => setSearchEtu(e.target.value)}
                placeholder="Rechercher par nom, prénom ou matricule…"
                className="w-full pl-9 pr-3 py-2 text-sm bg-gray-900 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500" />
            </div>
            <div className="max-h-56 overflow-y-auto bg-gray-900 border border-gray-800 rounded-lg">
              {etudiantsFiltres.length === 0 ? (
                <div className="px-3 py-4 text-xs text-gray-500 text-center">
                  Aucun étudiant pour {classeSel}.
                </div>
              ) : (
                etudiantsFiltres.map(e => {
                  const selected = matricule === e.matricule;
                  return (
                    <button key={e.matricule} type="button" onClick={() => setMatricule(e.matricule)}
                      className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 border-b border-gray-800/50 last:border-0 ${
                        selected
                          ? 'bg-blue-900/30 text-blue-200'
                          : 'text-gray-200 hover:bg-gray-800/60'
                      }`}>
                      <div className="w-7 h-7 rounded-full bg-blue-700/40 text-blue-200 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                        {(e.prenoms?.[0] || '') + (e.nom?.[0] || '')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="truncate font-medium">{e.nom} {e.prenoms}</div>
                        <div className="text-[10px] text-gray-500 font-mono">{e.matricule}</div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Sujet (optionnel) */}
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
            Sujet <span className="font-normal text-gray-500 normal-case">(optionnel)</span>
          </label>
          <input value={sujet} onChange={(e) => setSujet(e.target.value)}
            maxLength={200}
            className="w-full px-3 py-2 text-sm bg-gray-900 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500" />
        </div>

        {/* Matière (optionnel) */}
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
            Matière concernée <span className="font-normal text-gray-500 normal-case">(optionnel)</span>
          </label>
          <input value={matiereLibelle} onChange={(e) => setMatiereLibelle(e.target.value)}
            placeholder="Ex. Algorithmique" maxLength={200}
            className="w-full px-3 py-2 text-sm bg-gray-900 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500" />
        </div>

        {/* Contenu */}
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
            Message <span className="text-red-400">*</span>
          </label>
          <textarea value={contenu} onChange={(e) => setContenu(e.target.value)}
            rows={6} maxLength={5000} required
            className="w-full px-3 py-2 text-sm bg-gray-900 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 resize-none" />
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-800 text-red-200 rounded-lg p-2 text-sm">
            {error}
          </div>
        )}

        <button type="submit" disabled={isLoading}
          className="w-full px-4 py-3 text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg inline-flex items-center justify-center gap-2">
          {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {isLoading ? 'Envoi…' : 'Envoyer'}
        </button>
      </form>
    </FormateurLayout>
  );
}

// ──────────────────────────────────────────────────────────────────
// Détail thread
// ──────────────────────────────────────────────────────────────────

function ThreadView({ selection, onBack }: {
  selection: { kind: 'etudiant'; matricule: string; libelle: string }
           | { kind: 'classe'; classe: string };
  onBack: () => void;
}) {
  const params = selection.kind === 'etudiant'
    ? { matricule: selection.matricule }
    : { classe: selection.classe };
  const { data, isLoading } = useMessageThreadFormateurQuery(params);

  return (
    <FormateurLayout title={selection.kind === 'classe' ? `Classe ${selection.classe}` : selection.libelle}>
      <button onClick={onBack}
        className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1 mb-3">
        <ArrowLeft size={13} /> Retour
      </button>

      {isLoading && (
        <div className="flex items-center justify-center gap-2 py-10 text-gray-400 text-sm">
          <Loader2 size={16} className="animate-spin" /> Chargement…
        </div>
      )}

      {data && (
        <div className="space-y-2">
          {data.results.map(m => {
            const parFormateur = m.auteur_type === 'formateur';
            return (
              <div key={m.id} className={`flex ${parFormateur ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-xl ${
                  parFormateur
                    ? 'bg-blue-600/20 border border-blue-700/50'
                    : 'bg-gray-800 border border-gray-700'
                }`}>
                  {m.sujet && (
                    <div className="text-[11px] font-semibold text-gray-300 mb-1">
                      {m.sujet}
                    </div>
                  )}
                  <div className="text-sm text-gray-100 whitespace-pre-wrap">{m.contenu}</div>
                  <div className="text-[10px] text-gray-500 mt-1.5 flex items-center gap-2">
                    {!parFormateur && <span>{m.auteur_nom}</span>}
                    <span>{fmtDate(m.date_envoi)}</span>
                    {m.portee === 'classe' && (
                      <span className="inline-flex items-center gap-0.5 text-purple-400">
                        <UsersIcon size={9} /> Classe
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </FormateurLayout>
  );
}
